<template>
  <nav
    class="floating-menu-container"
    aria-label="Hauptnavigation"
  >
    <div class="menu-items">
      <div
        v-for="item in visibleItems"
        :key="item.route"
        class="menu-item"
        :class="{ 'menu-item-corner': item.corner }"
        :style="{ '--menu-delay': item.delay }"
      >
        <v-tooltip
          :text="item.label"
          location="end"
          :disabled="true"
        >
          <template #activator="{ props: tooltipProps }">
            <button
              v-bind="tooltipProps"
              type="button"
              class="menu-button"
              :class="{ 'menu-button-active': isRouteActive(item) }"
              :aria-label="item.label"
              :aria-current="isRouteActive(item) ? 'page' : undefined"
              @click="go(item.route)"
            >
              <v-icon
                :icon="item.icon"
                class="menu-icon"
                size="22"
              />

              <span class="menu-label">
                {{ item.label }}
              </span>
            </button>
          </template>
        </v-tooltip>
      </div>
    </div>
  </nav>
</template>

<script setup>
/*
 * Darstellung wie auf `master`, Einträge wie in diesem Branch.
 *
 * `master` hat die Navigation überarbeitet — semantisches `<nav>`, Beschriftung
 * die beim Überfahren ausfährt statt zu erscheinen, und durchgehend
 * Theme-Farben. Dieser Branch hing noch auf der Vorgängerfassung: fest
 * verdrahtete Grautöne, `color: #000` für die Icons, und ein Knopf, der beim
 * Überfahren vom Kreis zum abgerundeten Rechteck wurde.
 *
 * Übernommen wurde die Gestaltung, **nicht** die Einträge: `master` kennt
 * sechs, dieser Branch zwölf — darunter VALERI, Energiebilanz, Benchmarking
 * und Berichte. Masters Liste zu übernehmen hätte genau die Seiten aus der
 * Navigation entfernt, für die es diesen Branch gibt.
 */
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/projectStore'
import { PROJECT_PAGES } from '@/config/projectPages'

const router = useRouter()
const route = useRoute()
const store = useProjectStore()

const title = computed(() => store.title)

const itemsNewProject = [
  {
    icon: 'mdi-home',
    label: 'Startseite',
    delay: '0ms',
    route: 'Home',
  },
  {
    icon: 'mdi-format-list-bulleted',
    label: 'Projekte',
    delay: '80ms',
    route: 'ProjectList',
  },
]

// Index of "Maßnahmen" within the page sequence; "Neue Maßnahme" (a feature, not
// a page) is placed right after it in the navbar without touching PROJECT_PAGES.
const massnahmenIndex = PROJECT_PAGES.findIndex((page) => page.route === 'ProjectDashboard')

const items = [
  // Global
  { icon: 'mdi-home', label: 'Startseite', route: 'Home' },
  { icon: 'mdi-format-list-bulleted', label: 'Projekte', route: 'ProjectList' },
  // Projekt-Seiten bis einschließlich Maßnahmen (Reihenfolge = prev/next-Navigator)
  ...PROJECT_PAGES.slice(0, massnahmenIndex + 1),
  // Aktion: Neue Maßnahme – direkt nach Maßnahmen, aber nicht Teil der Seiten-Sequenz
  { icon: 'mdi-file-plus', label: 'Neue Maßnahme', route: 'NewAction' },
  // restliche Projekt-Seiten (Einsparpotential, Energiebilanz, …)
  ...PROJECT_PAGES.slice(massnahmenIndex + 1),
  /*
   * Projektkopie senden steht in der unteren Ecke, abgesetzt von den Seiten.
   *
   * Es ist keine Seite des Projekts, sondern eine Weitergabe nach außen —
   * zwischen „Berichte" und den Projektseiten gelesen zu werden, legt eine
   * Verwandtschaft nahe, die es nicht gibt. `corner` schiebt den Eintrag über
   * `margin-top: auto` an das untere Ende der Spalte.
   */
  {
    icon: 'mdi-share-variant-outline',
    label: 'Projektkopie senden',
    route: 'ShareProject',
    corner: true,
  },
].map((item, index) => ({ ...item, delay: `${index * 60}ms` }))

const visibleItems = computed(() => (title.value ? items : itemsNewProject))

const projectScopedRoutes = new Set([
  'ProjectDashboard',
  'ProjectDetails',
  'ProjectCalc',
  'ProjectBenchmarking',
  'ProjectCalcValeri',
  'ProjectEnergyFlow',
  'ProjectReports',
  'NewAction',
  'ShareProject',
  'Action',
])

/*
 * Hervorhebung des aktuellen Eintrags.
 *
 * Bearbeiten und Neuanlegen einer Maßnahme sind dieselbe Seite — die Routen
 * 'Action' und 'NewAction' rendern beide Action.vue — deshalb hebt sich für
 * beide derselbe Eintrag hervor statt der Maßnahmen-Liste.
 */
function isRouteActive(item) {
  if (route.name === item.route) return true
  if (item.route === 'NewAction' && route.name === 'Action') return true
  return false
}

const go = (name) => {
  if (route.name === name) return
  const projectId = route.params?.projectId
  if (projectScopedRoutes.has(name) && projectId) {
    return router.push({ name, params: { projectId } })
  }
  return router.push({ name })
}
</script>

<style scoped lang="scss">
/* ========================================
   Navigation container
======================================== */

.floating-menu-container {
  position: sticky;
  top: 5rem;
  left: 0;
  z-index: 20;

  width: 56px;
}

/* ========================================
   Menu list
======================================== */

.menu-items {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

/*
 * Der Eckeintrag haftet am Schirm, nicht am Ende der Liste.
 *
 * Zuerst über eine Spalte auf Schirmhöhe und `margin-top: auto` gelöst — das
 * war falsch und im Browser nachgemessen: die Höhe rechnet den Fusszeilen-
 * balken nicht mit. Auf 1920x1080 lag der Knopf unter der Fusszeile und war
 * nicht mehr anklickbar (`elementFromPoint` lieferte `FOOTER`), auf 1440x760
 * stand er ganz unterhalb des sichtbaren Bereichs.
 *
 * `--v-layout-bottom` ist die Höhe der Fusszeile aus Vuetifys Layout; der
 * Rückfallwert greift, falls die Variable einmal fehlt.
 */
.menu-item-corner {
  margin-top: 0.3rem;
}

/*
 * Nur wenn der Schirm hoch genug ist.
 *
 * Die Spalte führt zehn Einträge und reicht ab Oberkante bis etwa 710 px. Bei
 * 1440x760 liegt ihr Ende bereits auf der Fusszeile — dort ist keine Ecke mehr
 * frei, und ein gehefteter Knopf läge auf den Symbolen darüber. Unterhalb der
 * Schwelle bleibt der Eintrag deshalb am Ende der Liste.
 */
@media (min-height: 860px) {
  .menu-item-corner {
    position: fixed;
    left: 1rem;
    bottom: calc(var(--v-layout-bottom, 60px) + 1rem);

    margin-top: 0;
    z-index: 21;
  }
}

.menu-item {
  animation: menu-enter 220ms ease-out both;
  animation-delay: var(--menu-delay);
}

/* ========================================
   Menu buttons
======================================== */

.menu-button {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.65rem;

  width: 52px;
  min-width: 52px;
  height: 52px;
  padding: 0 15px;

  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 999px;

  color: rgb(var(--v-theme-on-surface));
  background:
    linear-gradient(
      145deg,
      rgba(var(--v-theme-surface-bright), 0.98),
      rgba(var(--v-theme-surface-variant), 0.96)
    );

  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);

  cursor: pointer;

  transition:
    width 220ms ease,
    color 160ms ease,
    background-color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.menu-button:hover {
  width: max-content;
  min-width: 52px;
  max-width: 230px;
  padding-right: 1.1rem;

  border-color: rgba(var(--v-theme-primary), 0.38);

  background:
    linear-gradient(
      145deg,
      rgba(var(--v-theme-primary), 0.15),
      rgba(var(--v-theme-primary), 0.07)
    ),
    rgb(var(--v-theme-surface));

  box-shadow:
    0 8px 22px rgba(0, 0, 0, 0.16),
    inset 3px 0 0 rgb(var(--v-theme-primary));

  transform: translateX(2px);
}

.menu-button:active {
  transform: translateX(2px) scale(0.97);
}

.menu-button:focus-visible {
  outline: 3px solid rgba(var(--v-theme-primary), 0.38);
  outline-offset: 3px;
}

/* ========================================
   Active route
======================================== */

.menu-button-active {
  color: rgb(var(--v-theme-on-primary));
  border-color: rgba(var(--v-theme-primary), 0.78);

  background:
    linear-gradient(
      145deg,
      rgba(var(--v-theme-primary), 0.95),
      rgba(var(--v-theme-primary), 0.78)
    );

  box-shadow:
    0 7px 20px rgba(0, 0, 0, 0.18),
    inset 3px 0 0 rgba(255, 255, 255, 0.58);
}

.menu-button-active:hover {
  color: rgb(var(--v-theme-on-primary));

  background:
    linear-gradient(
      145deg,
      rgb(var(--v-theme-primary)),
      rgba(var(--v-theme-primary), 0.82)
    );
}

/* ========================================
   Icon and label
======================================== */

.menu-icon {
  flex-shrink: 0;
  color: inherit;
}

.menu-label {
  max-width: 0;
  overflow: hidden;
  opacity: 0;

  color: inherit;

  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;

  transition:
    max-width 220ms ease,
    opacity 140ms ease;
}

.menu-button:hover .menu-label,
.menu-button:focus-visible .menu-label {
  max-width: 170px;
  opacity: 1;
}

/* ========================================
   Entrance animation
======================================== */

@keyframes menu-enter {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.94);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Respect reduced-motion preferences */
@media (prefers-reduced-motion: reduce) {
  .menu-item {
    animation: none;
  }

  .menu-button,
  .menu-label {
    transition: none;
  }
}

/* ========================================
   Horizontal navigation

   Auch bei **niedrigem** Schirm, nicht nur bei schmalem: die Spalte führt
   zehn Einträge und endet bei rund 710 px. Auf 1366x768 — einem verbreiteten
   Notebook — liegt damit schon der letzte Eintrag hinter der Fusszeile. Das
   war vor dieser Änderung so und fiel nur nicht auf, weil der betroffene
   Eintrag „Berichte" hiess und niemand ihn dort suchte.

   Die Leiste oben scrollt waagerecht und zeigt alle Einträge. Scrollen in der
   senkrechten Spalte wäre keine Alternative: `overflow-y` erzwingt auch
   waagerechtes Abschneiden und würde die ausfahrenden Beschriftungen kappen.
======================================== */

@media (max-width: 1300px), (max-height: 859px) {
  .floating-menu-container {
    position: sticky;
    top: calc(var(--v-layout-top, 64px) + 8px);
    left: 0;
    z-index: 20;

    width: 100%;
    padding: 0.4rem 0.65rem;

    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 18px;

    background-color: rgba(var(--v-theme-surface), 0.96);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);

    backdrop-filter: blur(10px);
  }

  .menu-items {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    overflow-x: auto;
    overflow-y: hidden;

    padding: 0.15rem;

    scrollbar-width: thin;
    overscroll-behavior-inline: contain;
    scroll-snap-type: x proximity;
  }

  .menu-item {
    flex: 0 0 auto;
    animation: none;
    scroll-snap-align: start;
  }

  /* Quer gibt es keine untere Ecke — der Eintrag steht am Ende der Leiste. */
  .menu-item-corner {
    position: static;
    margin-left: auto;
  }

  .menu-button {
    width: 42px;
    min-width: 42px;
    height: 42px;
    padding: 0 10px;
  }

  .menu-button:hover,
  .menu-button:focus-visible {
    width: max-content;
    max-width: 220px;
    padding-right: 0.9rem;
    transform: none;
  }

  .menu-button:active {
    transform: scale(0.97);
  }
}

/* ========================================
   Touch devices

   Hover expansion is unreliable on touch.
   Keep compact icon buttons and use aria-label.
======================================== */

@media (hover: none) {
  .menu-button:hover {
    width: 42px;
    min-width: 42px;
    padding-right: 10px;
  }

  .menu-button:hover .menu-label {
    max-width: 0;
    opacity: 0;
  }

  .menu-button:focus-visible {
    width: max-content;
    max-width: 220px;
    padding-right: 0.9rem;
  }

  .menu-button:focus-visible .menu-label {
    max-width: 170px;
    opacity: 1;
  }
}

/* ========================================
   Very small screens
======================================== */

@media (max-width: 600px) {
  .floating-menu-container {
    padding: 0.35rem 0.5rem;
    border-radius: 16px;
  }

  .menu-items {
    gap: 0.4rem;
  }

  .menu-button {
    width: 40px;
    min-width: 40px;
    height: 40px;
    padding-inline: 9px;
  }
}
</style>
